import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AddToCartComponent } from '../add-to-cart/add-to-cart.component';
import { DOCUMENT_IMAGE_DESCRIPTIONS, DOCUMENT_IMAGE_INCLUDES } from '@root/reports/shared/constants/reports.constants';
import { CartInfo, CartItem, CartItemState } from '@store/orders/orders.model';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { AddToCartDocumentImage } from '@store/orders/orders.actions';
import { selectSelectedPropertyInformation, selectSubHeaderHeader } from '@store/reports/reports.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReportHeader } from '@store/reports/reports.model';
import { PropertyIdentifier, PropertyInformation } from '@store/search-board/search-board.model';
import { CartItemKeys } from '@root/orders/components/cart/cart.constants';
import { selectDocumentImageOffer } from '@store/orders/orders.selector';
import { filter, map, Observable } from 'rxjs';
import { ViewConfirmationComponent } from '@root/orders/components/view-confirmation/view-confirmation.component';
import { ModalType } from '@shared/models/modal.model';

@UntilDestroy()
@Component({
  selector: 'rlst-document-image-modal',
  standalone: true,
  imports: [
    CommonModule,
    AddToCartComponent,
  ],
  templateUrl: './document-image-modal.component.html',
  styleUrls: ['./document-image-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentImageModalComponent implements OnInit {
  public reportIncludes: Array<string> = DOCUMENT_IMAGE_INCLUDES;
  public descriptions: Array<string> = DOCUMENT_IMAGE_DESCRIPTIONS;
  private header: ReportHeader;
  private propertyIdentifier: PropertyIdentifier;
  public cartItem: CartItem;
  public documentImageOffer$: Observable<CartItem>;

  constructor(
    private readonly store$: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data: {
      reportAvailableToView: boolean,
      documentNumber: string,
      params: string,
      reportExists?: boolean,
    },
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<DocumentImageModalComponent>,
    private readonly viewContainerRef: ViewContainerRef
  ) { }

  public ngOnInit(): void {

    this.documentImageOffer$ = this.store$.pipe(
      select(selectDocumentImageOffer),
      untilDestroyed(this),
      filter(Boolean),
      map((cartInfo: CartInfo) => {
        return cartInfo.savedItems.find(
          (cartItem: CartItem) => cartItem.productId === CartItemKeys.DOCUMENT_IMAGE && cartItem.apn === this.data.documentNumber
        )
      }),
      filter(Boolean),
    )

    this.documentImageOffer$.subscribe((cartItem: CartItem) => {
      this.cartItem = cartItem;
    });

    this.store$.pipe(
      select(selectSelectedPropertyInformation),
      untilDestroyed(this),
    ).subscribe((propertyInformation: PropertyInformation) => {
      this.propertyIdentifier = propertyInformation.propertyIdentifier;
    });

    this.store$.pipe(
      select(selectSubHeaderHeader),
      untilDestroyed(this),
    ).subscribe((header: ReportHeader) => this.header = header);
  }

  public addToCart(): void {
    const newCartItem: CartItem = {
      ...this.cartItem,
      state: CartItemState.IN_CART,
      requestPayloadPartial: this.header.address,
      requestPayloadFull: JSON.stringify({
        'parcelId': this.propertyIdentifier.parcelId,
        'fipsCode': this.propertyIdentifier.fipsCode,
        'apn': this.data.documentNumber,
        'universalParcelId': this.propertyIdentifier.universalParcelId,
        'documentImageParam': this.data.params
      })
    }
    this.store$.dispatch(new AddToCartDocumentImage(newCartItem));
    this.dialogRef.close();
  }

  public viewReport(): void {
    this.dialog.open(ViewConfirmationComponent, {
      panelClass: [...ModalType.CONFIRMATION, 'bg-s'],
      data: { property: JSON.stringify(this.propertyIdentifier), orderItem: this.cartItem, isReportPage: true, documentImageParam: this.data.params },
      viewContainerRef: this.viewContainerRef,
    }).afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((isClose: boolean) => {
        if (!isClose) {
          this.dialogRef.close();
        }
      });
  }

  public close(): void {
    this.dialogRef.close();
  }
}
